// All Reservations lifecycle sub-tabs. Keys are the API `status` values; labels are the UI text.
export const STATUS_TABS = {
  upcoming: { label: "Upcoming" },
  requested: { label: "Requests" },
  completed: { label: "Completed" },
  canceled: { label: "Canceled" }
}

// Canonical list of the reservation status values (single source of truth for PropTypes etc.).
export const RESERVATION_STATUSES = Object.keys(STATUS_TABS)

// Sub-tabs the backend serves as future-only (booked_date >= today): past dates are blocked
// and a carried-over past date snaps forward. Change here to adjust every date restriction.
export const FUTURE_ONLY_STATUSES = ["upcoming", "requested"]

// Sort the listings fall back to when no column is actively sorted — including after the user
// cancels a sort (Ant Design's third header click). The request always carries a sort, but with
// no column key set no header renders as sorted.
export const DEFAULT_SORT_KEY = "booked_date"
export const DEFAULT_SORT_ORDER = "asc"

export const REQUEST_TYPES = {
  upcoming: "booked",
  requested: "requested",
  completed: "completed",
  canceled: "canceled"
}

export const PAYMENT_STATUS = {
  partial: "partial",
  paid: "paid",
  partial_refund: "partial_refund"
}

export const getTimePeriods = (t) => ({
  day: t("bookings.day"),
  week: t("bookings.week"),
  month: t("bookings.month")
})

export const getStatusMessages = (t) => ({
  booked: {
    title: t("bookings.upcoming_title"),
    description: t("bookings.upcoming_description")
  },
  requested: {
    title: t("bookings.requests_title"),
    description: t("bookings.requests_description")
  },
  completed: {
    title: t("bookings.completed_title"),
    description: t("bookings.completed_description")
  },
  canceled: {
    title: t("bookings.canceled_title"),
    description: t("bookings.canceled_description")
  }
})

export const PAYMENT_OPTIONS = {
  deposit_later: "deposit_and_balance_later",
  deposit_cash: "deposit_and_balance_in_cash",
  full_now: "full_amount_today",
  full_cash: "full_amount_in_cash_on_day",
  full_link: "full_amount_by_link",
  deposit_link: "deposit_by_link_and_balance_later",
  deposit_link_in_cash: "deposit_by_link_and_balance_in_cash",
  pay_later: "pay_later"
}

export const initialValues = {
  isExistingClient: false,
  existing_client: null,
  firstName: "",
  lastName: "",
  email: "",
  phone_number: null,
  experience: null,
  date: null,
  duration: null,
  time: null,
  client_notify: true,
  accessibility_required: false,
  accessibility_notes: "",
  notes: "",
  depositOnly: false,
  depositPercent: null,
  customDepositAmount: null,
  paymentOption: "",
  paymentType: undefined,
  collectPayment: "yes",
  recordPayment: 0,
  refundBy: ""
}

export const REFUND_CANCEL_REASONS = ["Equipment Issues", "Weather", "Pro or Employee Unavailable"]

// Payment collection labels rendered with a custom style (white surface, #8F4700 text)
export const OPERATOR_TO_COLLECT_LABELS = ["Operator To Collect", "Operator to Collect"]

export const BOOKING_SOURCE = {
  marketplace: "marketplace",
  manual: "manual",
  embedded: "embedded",
  website: "website"
}

// Width of the New / Edit Booking window on desktop (Figma 5794:87784).
export const BOOKING_WINDOW_WIDTH = 800

// Width of the "Reservation is Confirmed" modal shown once a New Booking is saved (Figma 7378:129723).
export const BOOKING_CONFIRMATION_WIDTH = 600

// The form fields one experience of a New Booking owns. New Booking's "Add Another Experience"
// saves these (with the experience's bookingSelections) to the Added Experiences list and clears them.
export const EXPERIENCE_FORM_FIELDS = [
  "experience",
  "date",
  "duration",
  "time",
  "main",
  "totalGuests",
  "accessibility_required",
  "accessibility_notes",
  "notes"
]

// Those fields, and the experience's bookingSelections, with nothing chosen.
export const EMPTY_EXPERIENCE_FORM_VALUES = {
  experience: null,
  date: null,
  duration: null,
  time: null,
  main: { adults: 0, children: 0, infants: 0, passengers: 0 },
  totalGuests: undefined,
  accessibility_required: false,
  accessibility_notes: "",
  notes: ""
}

export const EMPTY_EXPERIENCE_SELECTIONS = {
  experienceUuid: null,
  date: null,
  endDate: null,
  timeSlot: null,
  tripLength: null,
  counts: null,
  amount: 0,
  baseAmount: 0,
  originalBaseAmount: 0,
  breakdown: [],
  preCardTotal: 0,
  preCardTotalBeforeAdjustments: 0,
  cardProcessingFee: 0,
  addOns: {},
  addOnsTotal: 0,
  // A ticketed experience's start time and tickets (TicketedFields).
  scheduledSlot: null,
  totalGuests: null,
  ticketCounts: null
}

// A price lower than its before-adjustments figure by less than this is rounding, not a discount
// (New Booking's struck guest prices and summary total).
export const MIN_PRICE_SAVING = 0.005

export const BOOKING_MODES = {
  embedded: "embedded",
  clientBooking: "clientBooking",
  guideProfile: "guideProfile"
}


// Multi-day calendar block reasons (booking_details available_slots) the guide's calendar leaves
// unexplained: a blackout day itself (QA Multiday #21). `span_blackout` — a free day whose stay
// would run into a blackout — still explains itself, or the day looks disabled for no reason
// (QA Ticketing #47).
export const UNEXPLAINED_BLOCK_REASONS = ["blackout"]
